import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import { TrendingUp, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { creditFactors, creditScoreHistory } from '../../../data/mockData';
function CreditGauge({
  score
}: {
  score: number;
}) {
  const min = 300,
    max = 850;
  const pct = (score - min) / (max - min) * 100;
  const angle = -135 + pct / 100 * 270;
  const getColor = (s: number) => s >= 750 ? '#39E75F' : s >= 700 ? '#1566FF' : s >= 650 ? '#F59E0B' : '#EF4444';
  const getLabel = (s: number) => s >= 750 ? 'Excellent' : s >= 700 ? 'Very Good' : s >= 650 ? 'Good' : 'Fair';
  const color = getColor(score);
  const label = getLabel(score);
  return <div className="flex flex-col items-center" data-id="element-299">
      <div className="relative w-48 h-32 overflow-hidden" data-id="element-300">
        <svg viewBox="0 0 200 120" className="w-full h-full" data-id="element-301">
          {/* Background arc */}
          <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#E5E7EB" strokeWidth="16" strokeLinecap="round" className="dark:stroke-gray-700" data-id="element-302" />
          {/* Score arc */}
          <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke={color} strokeWidth="16" strokeLinecap="round" strokeDasharray={`${pct * 2.51} 251`} style={{
          transition: 'stroke-dasharray 1.5s ease'
        }} data-id="element-303" />
          {/* Needle */}
          <line x1="100" y1="100" x2={100 + 60 * Math.cos((angle - 90) * Math.PI / 180)} y2={100 + 60 * Math.sin((angle - 90) * Math.PI / 180)} stroke={color} strokeWidth="3" strokeLinecap="round" data-id="element-304" />
          <circle cx="100" cy="100" r="6" fill={color} data-id="element-305" />
          {/* Labels */}
          <text x="20" y="118" fontSize="10" fill="#9CA3AF" textAnchor="middle" data-id="element-306">
            300
          </text>
          <text x="180" y="118" fontSize="10" fill="#9CA3AF" textAnchor="middle" data-id="element-307">
            850
          </text>
        </svg>
      </div>
      <div className="text-center -mt-2" data-id="element-308">
        <p className="text-5xl font-black" style={{
        color
      }} data-id="element-309">
          {score}
        </p>
        <p className="text-sm font-semibold mt-1" style={{
        color
      }} data-id="element-310">
          {label}
        </p>
        <p className="text-xs text-gray-400 mt-0.5" data-id="element-311">Updated Feb 20, 2026</p>
      </div>
    </div>;
}
const impactColors = {
  high: 'text-red-500',
  medium: 'text-amber-500',
  low: 'text-gray-400'
};
const impactBg = {
  high: 'bg-red-50 dark:bg-red-900/20',
  medium: 'bg-amber-50 dark:bg-amber-900/20',
  low: 'bg-gray-50 dark:bg-dark-hover'
};
export function CreditView() {
  return <div className="space-y-6" data-id="element-312">
      {/* Score card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-313">
        <div className="bg-white dark:bg-dark-card rounded-2xl p-6 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-314">
          <div className="flex items-center justify-between mb-4" data-id="element-315">
            <div data-id="element-316">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-317">
                Credit Score
              </h3>
              <p className="text-xs text-gray-400" data-id="element-318">VantageScore 3.0</p>
            </div>
            <span className="flex items-center gap-1 text-xs bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-400 px-2 py-1 rounded-full font-medium" data-id="element-319">
              <TrendingUp size={11} data-id="element-320" /> +44 this year
            </span>
          </div>
          <CreditGauge score={742} data-id="element-321" />
        </div>

        {/* Score history */}
        <div className="bg-white dark:bg-dark-card rounded-2xl p-6 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-322">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1" data-id="element-323">
            Score History
          </h3>
          <p className="text-xs text-gray-400 mb-4" data-id="element-324">Last 12 months</p>
          <ResponsiveContainer width="100%" height={180} data-id="element-325">
            <LineChart data={creditScoreHistory} data-id="element-326">
              <XAxis dataKey="month" tick={{
              fontSize: 11,
              fill: '#9CA3AF'
            }} axisLine={false} tickLine={false} data-id="element-327" />
              <YAxis tick={{
              fontSize: 11,
              fill: '#9CA3AF'
            }} axisLine={false} tickLine={false} domain={[680, 760]} data-id="element-328" />
              <Tooltip formatter={(v: any) => [v, 'Score']} contentStyle={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              fontSize: '12px'
            }} data-id="element-329" />
              <Line type="monotone" dataKey="score" stroke="#1566FF" strokeWidth={2.5} dot={{
              fill: '#1566FF',
              r: 3
            }} activeDot={{
              r: 5
            }} data-id="element-330" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Score factors */}
      <div className="bg-white dark:bg-dark-card rounded-2xl p-6 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-331">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" data-id="element-332">
          Score Factors
        </h3>
        <div className="space-y-4" data-id="element-333">
          {creditFactors.map((factor, i) => <div key={i} data-id="element-334">
              <div className="flex items-center justify-between mb-2" data-id="element-335">
                <div className="flex items-center gap-2" data-id="element-336">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-337">
                    {factor.name}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${impactBg[factor.impact]} ${impactColors[factor.impact]}`} data-id="element-338">
                    {factor.impact} impact
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-339">
                  {factor.score}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-dark-hover rounded-full overflow-hidden mb-1" data-id="element-340">
                <div className="h-full rounded-full transition-all duration-700" style={{
              width: `${factor.score}%`,
              backgroundColor: factor.score >= 80 ? '#39E75F' : factor.score >= 60 ? '#1566FF' : '#F59E0B'
            }} data-id="element-341" />
              </div>
              <p className="text-xs text-gray-400" data-id="element-342">{factor.description}</p>
            </div>)}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-2xl p-6 border border-primary-200 dark:border-primary-800" data-id="element-343">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2" data-id="element-344">
          <Info size={16} className="text-primary-500" data-id="element-345" /> Tips to Improve Your
          Score
        </h3>
        <div className="space-y-2" data-id="element-346">
          {['Pay down your credit card balance by $500 to reduce utilization below 20%', 'Keep your oldest credit account open — it helps your credit age', 'Avoid applying for new credit in the next 6 months', 'Set up autopay to ensure 100% on-time payment history'].map((tip, i) => <div key={i} className="flex items-start gap-2" data-id="element-347">
              <CheckCircle size={14} className="text-primary-500 mt-0.5 shrink-0" data-id="element-348" />
              <p className="text-xs text-gray-700 dark:text-gray-300" data-id="element-349">{tip}</p>
            </div>)}
        </div>
      </div>
    </div>;
}